import type {
  CourseGrabUsers,
  CourseGrabGrantInput,
  CourseGrabAdjustmentInput,
  CourseGrabChange,
} from "./course-grab";
import type {
  WechatPaymentOrder,
  WechatRefund,
  WechatRefundQuote,
  AdminAuthModeResponse,
  AdminSessionResponse,
  AutoDormCheckGrantCampaign,
  AutoDormCheckGrantInput,
  AutoDormCheckGrantPreview,
  AutoDormCheckIn,
  AutoDormCheckPlan,
  AutoDormCheckPlanUpdateInput,
  AutoDormCheckQuotaAdjustmentInput,
  AutoDormCheckQuotaAdjustmentResult,
  AutoDormCheckQuotaLedgerPeriodMode,
  AutoDormCheckQuotaLedgerResult,
  AutoDormCheckQuotaLedgerType,
  AutoDormCheckQuotaRevokeResult,
  AutoDormCheckQuotaUser,
  AutoDormCheckQuotaUserDetail,
  AutoDormCheckRunResult,
  AutoDormCheckSettings,
  AutoDormCheckTask,
  AutoDormCheckUserFilter,
  AutoDormCheckUserResult,
  AuditLevel,
  AuditResult,
  CalendarRecord,
  CreatePublicationInput,
  FeedbackResult,
  FeedbackType,
  JobState,
  Overview,
  OverviewApplicationSection,
  OverviewDatabaseSection,
  OverviewDependenciesSection,
  OverviewRedisSection,
  OverviewSessionsSection,
  OverviewStorageSection,
  Paginated,
  PublicationDetail,
  PublicationCourseCatalog,
  PublicationMedia,
  PublicationRecipient,
  PublicationSummary,
  SecurityProblemUser,
  SecurityRequestEvent,
  SnapshotResource,
  SnapshotOwner,
  SnapshotRow,
  StorageOverview,
  UserDetail,
  UserMajor,
  MapLocatorConfig,
  MapLocatorPeople,
  UserSummary,
  UserFeedback,
  WatermarkDecodeResult,
  VpnStatus,
} from "./types";

import type {
  LerunSettings,
  LerunOverview,
  LerunCdks,
  LerunVisits,
  LerunUserDetail,
  LerunListQuery,
} from "./lerun-types";
const ADMIN_ROOT = "/api/v1/admin";
const ADMIN_REQUEST_HEADER = "easy-swu-admin";

interface Envelope<T> {
  success: true;
  data: T;
}

interface ErrorEnvelope {
  success: false;
  error?: { code?: string; message?: string; details?: unknown };
  requestId?: string;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code = "REQUEST_FAILED",
    public readonly requestId?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export async function request<T>(
  path: string,
  options: RequestInit & { suppressUnauthorizedEvent?: boolean } = {},
): Promise<T> {
  const headers = new Headers(options.headers);
  headers.set("Accept", "application/json");
  headers.set("X-Admin-Request", ADMIN_REQUEST_HEADER);
  const binaryBody = options.body instanceof Blob;
  if (
    options.body &&
    !(options.body instanceof FormData) &&
    !binaryBody &&
    !headers.has("Content-Type")
  ) {
    headers.set("Content-Type", "application/json");
  }
  if (binaryBody && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/octet-stream");
  }
  const response = await fetch(`${ADMIN_ROOT}${path}`, {
    ...options,
    credentials: "include",
    headers,
  });
  const payload = (await response.json().catch(() => null)) as
    Envelope<T> | ErrorEnvelope | null;
  if (!response.ok || !payload || !payload.success) {
    const failure = payload as ErrorEnvelope | null;
    if (response.status === 401 && !options.suppressUnauthorizedEvent) {
      window.dispatchEvent(new CustomEvent("easy-swu:admin-unauthorized"));
    }
    throw new ApiError(
      failure?.error?.message || `请求失败（HTTP ${response.status}）`,
      response.status,
      failure?.error?.code,
      failure?.requestId,
    );
  }
  return payload.data;
}

function query(values: Record<string, string | number | undefined>): string {
  const params = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => {
    if (value !== undefined && value !== "") params.set(key, String(value));
  });
  const encoded = params.toString();
  return encoded ? `?${encoded}` : "";
}

export const api = {
  recognitionUsers(
    input: import("./schedule-recognition").RecognitionFilter & {
      page: number;
      pageSize: number;
    },
  ) {
    return request<import("./schedule-recognition").RecognitionUsers>(
      `/schedule-recognitions/users${query(input)}`,
    );
  },
  recognitionJobs(
    userId: string,
    input: import("./schedule-recognition").RecognitionFilter & {
      page: number;
      pageSize: number;
    },
  ) {
    return request<Paginated<import("./schedule-recognition").RecognitionJob>>(
      `/schedule-recognitions/users/${encodeURIComponent(userId)}/jobs${query(input)}`,
    );
  },
  recognitionDetail(id: string) {
    return request<import("./schedule-recognition").RecognitionDetail>(
      `/schedule-recognitions/${encodeURIComponent(id)}`,
    );
  },
  setRecognitionAccess(userId: string, enabled: boolean) {
    return request<{ userId: string; enabled: boolean }>(
      `/schedule-recognitions/users/${encodeURIComponent(userId)}/access`,
      { method: "PATCH", body: JSON.stringify({ enabled }) },
    );
  },
  previewLerunGrant(input: AutoDormCheckGrantInput) {
    return request<AutoDormCheckGrantPreview>("/lerun/grants/preview", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  createLerunGrant(input: AutoDormCheckGrantInput, key: string) {
    return request<{
      id: string;
      initialRecipientCount: number;
      includeFuture: boolean;
    }>("/lerun/grants", {
      method: "POST",
      headers: { "Idempotency-Key": key },
      body: JSON.stringify(input),
    });
  },
  adjustLerunQuota(
    id: string,
    quotaAmount: number,
    reason: string,
    key: string,
  ) {
    return request("/lerun/users/" + encodeURIComponent(id) + "/quota", {
      method: "POST",
      headers: { "Idempotency-Key": key },
      body: JSON.stringify({ operation: "set", quotaAmount, reason }),
    });
  },
  lerunSettings() {
    return request<LerunSettings>("/lerun/settings");
  },
  lerunIssuer() {
    return request<import("./lerun-types").LerunIssuer>("/lerun/issuer");
  },
  refreshLerunIssuer() {
    return request<import("./lerun-types").LerunIssuer>(
      "/lerun/issuer/refresh",
      {
        method: "POST",
      },
    );
  },
  updateLerunSettings(
    input: Pick<LerunSettings, "entryEnabled" | "functionEnabled" | "version"> &
      Partial<
        Pick<
          LerunSettings,
          | "freeMode"
          | "invitationEnabled"
          | "checkinEnabled"
          | "invitationReward"
          | "checkinReward"
        >
      >,
  ) {
    return request<LerunSettings>("/lerun/settings", {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  },
  lerunOverview(days: number) {
    return request<LerunOverview>("/lerun/overview" + query({ days }));
  },
  lerunCdks(input: LerunListQuery) {
    return request<LerunCdks>("/lerun/cdks" + query(input));
  },
  lerunVisits(input: LerunListQuery) {
    return request<LerunVisits>("/lerun/visits" + query(input));
  },
  lerunVisitUsers(input: LerunListQuery) {
    return request<Paginated<import("./lerun-types").LerunVisitUser>>(
      "/lerun/visit-users" + query(input),
    );
  },
  lerunUser(id: string) {
    return request<LerunUserDetail>("/lerun/users/" + encodeURIComponent(id));
  },
  lerunAccounts(input: LerunListQuery) {
    return request<import("./lerun-types").LerunAccounts>(
      "/lerun/accounts" + query(input),
    );
  },
  lerunUserActivity(
    id: string,
    input: {
      page: number;
      pageSize: number;
      category?: "credits" | "agreement" | "process";
    },
  ) {
    return request<import("./lerun-types").LerunUserActivity>(
      "/lerun/users/" + encodeURIComponent(id) + "/activity" + query(input),
    );
  },
  authMode() {
    return request<AdminAuthModeResponse>("/auth-mode", {
      suppressUnauthorizedEvent: true,
    });
  },
  login(username: string, password: string) {
    return request<AdminSessionResponse>("/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
      suppressUnauthorizedEvent: true,
    });
  },
  logout() {
    return request<{ loggedOut: true }>("/logout", {
      method: "POST",
      suppressUnauthorizedEvent: true,
    });
  },
  ssoLoginUrl({ forceLogin = false }: { forceLogin?: boolean } = {}) {
    return `${ADMIN_ROOT}/oidc/login${forceLogin ? "?prompt=login" : ""}`;
  },
  ssoLogoutUrl() {
    return `${ADMIN_ROOT}/oidc/logout`;
  },
  session() {
    return request<AdminSessionResponse>("/session", {
      suppressUnauthorizedEvent: true,
    });
  },
  decodeWatermark(
    image: Blob,
    options: { fileName: string; processScale: number },
  ) {
    return request<WatermarkDecodeResult>("/watermarks/decode", {
      method: "POST",
      headers: {
        "X-File-Name": encodeURIComponent(options.fileName),
        "X-Image-Scale": String(options.processScale),
      },
      body: image,
    });
  },
  overview() {
    return request<Overview>("/overview");
  },
  overviewApplication() {
    return request<OverviewApplicationSection>("/overview/application");
  },
  overviewDatabase() {
    return request<OverviewDatabaseSection>("/overview/database");
  },
  overviewDependencies() {
    return request<OverviewDependenciesSection>("/overview/dependencies");
  },
  overviewRedis() {
    return request<OverviewRedisSection>("/overview/redis");
  },
  overviewSessions() {
    return request<OverviewSessionsSection>("/overview/sessions");
  },
  overviewStorage() {
    return request<OverviewStorageSection>("/overview/storage");
  },
  vpn() {
    return request<VpnStatus>("/vpn?probe=true");
  },
  users(options: { page?: number; pageSize?: number; q?: string } = {}) {
    return request<Paginated<UserSummary>>(`/users${query(options)}`);
  },
  mapLocatorConfig() {
    return request<MapLocatorConfig>("/map-locator/config");
  },
  mapLocatorPeople() {
    return request<MapLocatorPeople>("/map-locator/people");
  },
  securityUsers(
    options: { page?: number; pageSize?: number; q?: string } = {},
  ) {
    return request<Paginated<SecurityProblemUser>>(
      `/security/users${query(options)}`,
    );
  },
  securityEvents(
    userId: string,
    options: { page?: number; pageSize?: number } = {},
  ) {
    return request<Paginated<SecurityRequestEvent>>(
      `/security/users/${encodeURIComponent(userId)}/events${query(options)}`,
    );
  },
  deleteSecurityEvent(userId: string, eventId: string) {
    return request<{ deleted: true }>(
      `/security/users/${encodeURIComponent(userId)}/events/${encodeURIComponent(eventId)}`,
      { method: "DELETE" },
    );
  },
  userMajors() {
    return request<UserMajor[]>("/users/majors");
  },
  user(id: string) {
    return request<UserDetail>(`/users/${encodeURIComponent(id)}`);
  },
  autoDormCheckSettings() {
    return request<AutoDormCheckSettings>("/auto-dorm-check/settings");
  },
  courseGrabSettings() {
    return request<{
      enabled: boolean;
      maxUsers: number | null;
      version: number;
      updatedAt: string;
    }>("/course-grab/settings");
  },
  courseGrabUsers(options: {
    page: number;
    pageSize?: number;
    q?: string;
    filter?: "all" | "paid" | "quota";
  }) {
    return request<CourseGrabUsers>(`/course-grab/users${query(options)}`);
  },
  courseGrabGrantPreview(input: CourseGrabGrantInput) {
    return request<{
      recipientCount: number;
      totalUses: number;
      includeFuture: boolean;
    }>("/course-grab/grants/preview", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  courseGrabGrant(input: CourseGrabGrantInput, key: string) {
    return request<CourseGrabChange>("/course-grab/grants", {
      method: "POST",
      headers: { "Idempotency-Key": key },
      body: JSON.stringify(input),
    });
  },
  adjustCourseGrabUser(
    userId: string,
    input: CourseGrabAdjustmentInput,
    key: string,
  ) {
    return request<CourseGrabChange>(
      `/course-grab/users/${encodeURIComponent(userId)}/adjust`,
      {
        method: "POST",
        headers: { "Idempotency-Key": key },
        body: JSON.stringify(input),
      },
    );
  },
  updateCourseGrabSettings(input: {
    enabled?: boolean;
    maxUsers?: number | null;
    version: number;
  }) {
    return request<{
      enabled: boolean;
      maxUsers: number | null;
      version: number;
      updatedAt: string;
    }>("/course-grab/settings", {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  },
  updateAutoDormCheckSettings(
    input: Partial<
      Pick<
        AutoDormCheckSettings,
        | "entryEnabled"
        | "functionEnabled"
        | "paymentEnabled"
        | "checkInStartTime"
        | "checkInEndTime"
        | "schoolCheckInStartTime"
        | "schoolCheckInEndTime"
      >
    >,
  ) {
    return request<AutoDormCheckSettings>("/auto-dorm-check/settings", {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  },
  autoDormCheckPlans() {
    return request<{ items: AutoDormCheckPlan[] }>(
      "/auto-dorm-check/payment/plans",
    );
  },
  wechatOrders(
    page: number,
    q: string,
    status: string,
    product: "auto-dorm-check" | "course-grab" = "auto-dorm-check",
  ) {
    return request<Paginated<WechatPaymentOrder>>(
      `/${product}/payment/orders?${new URLSearchParams({ page: String(page), pageSize: "15", q, status })}`,
    );
  },
  wechatRefundQuote(
    orderId: string,
    product: "auto-dorm-check" | "course-grab" = "auto-dorm-check",
  ) {
    return request<WechatRefundQuote>(
      `/${product}/payment/orders/${encodeURIComponent(orderId)}/refund-preview`,
    );
  },
  refundWechatOrder(
    orderId: string,
    input: { reason: string; expectedAmountCents: number },
    idempotencyKey: string,
    product: "auto-dorm-check" | "course-grab" = "auto-dorm-check",
  ) {
    return request<WechatRefund>(
      `/${product}/payment/orders/${encodeURIComponent(orderId)}/refund`,
      {
        method: "POST",
        headers: { "Idempotency-Key": idempotencyKey },
        body: JSON.stringify(input),
      },
    );
  },
  updateAutoDormCheckPlan(planId: string, input: AutoDormCheckPlanUpdateInput) {
    return request<AutoDormCheckPlan>(
      `/auto-dorm-check/payment/plans/${encodeURIComponent(planId)}`,
      {
        method: "PATCH",
        body: JSON.stringify(input),
      },
    );
  },
  previewAutoDormCheckGrant(input: AutoDormCheckGrantInput) {
    return request<AutoDormCheckGrantPreview>(
      "/auto-dorm-check/grants/preview",
      {
        method: "POST",
        body: JSON.stringify(input),
      },
    );
  },
  createAutoDormCheckGrant(
    input: AutoDormCheckGrantInput,
    idempotencyKey: string,
  ) {
    return request<AutoDormCheckGrantCampaign>("/auto-dorm-check/grants", {
      method: "POST",
      headers: { "Idempotency-Key": idempotencyKey },
      body: JSON.stringify(input),
    });
  },
  autoDormCheckGrants(options: { page?: number; pageSize?: number } = {}) {
    return request<Paginated<AutoDormCheckGrantCampaign>>(
      `/auto-dorm-check/grants${query(options)}`,
    );
  },
  autoDormCheckQuotaUsers(
    options: { page?: number; pageSize?: number; q?: string } = {},
  ) {
    return request<Paginated<AutoDormCheckQuotaUser>>(
      `/auto-dorm-check/quota-users${query(options)}`,
    );
  },
  autoDormCheckQuotaLedger(
    options: {
      page?: number;
      pageSize?: number;
      q?: string;
      mode?: AutoDormCheckQuotaLedgerPeriodMode;
      period?: string;
      type?: AutoDormCheckQuotaLedgerType;
    } = {},
  ) {
    return request<AutoDormCheckQuotaLedgerResult>(
      `/auto-dorm-check/quota-ledger${query(options)}`,
    );
  },
  autoDormCheckQuotaUser(
    userId: string,
    options: {
      creditPage?: number;
      creditPageSize?: number;
      activityPage?: number;
      activityPageSize?: number;
    } = {},
  ) {
    return request<AutoDormCheckQuotaUserDetail>(
      `/auto-dorm-check/quota-users/${encodeURIComponent(userId)}${query(options)}`,
    );
  },
  revokeAutoDormCheckCredit(
    userId: string,
    creditId: string,
    reason: string,
    idempotencyKey: string,
  ) {
    return request<AutoDormCheckQuotaRevokeResult>(
      `/auto-dorm-check/quota-users/${encodeURIComponent(userId)}/credits/${encodeURIComponent(creditId)}/revoke`,
      {
        method: "POST",
        headers: { "Idempotency-Key": idempotencyKey },
        body: JSON.stringify({ reason }),
      },
    );
  },
  adjustAutoDormCheckQuota(
    userId: string,
    input: AutoDormCheckQuotaAdjustmentInput,
    idempotencyKey: string,
  ) {
    return request<AutoDormCheckQuotaAdjustmentResult>(
      `/auto-dorm-check/quota-users/${encodeURIComponent(userId)}/adjust`,
      {
        method: "POST",
        headers: { "Idempotency-Key": idempotencyKey },
        body: JSON.stringify(input),
      },
    );
  },
  autoDormCheckUsers(
    options: {
      page?: number;
      pageSize?: number;
      q?: string;
      filter?: AutoDormCheckUserFilter;
    } = {},
  ) {
    return request<AutoDormCheckUserResult>(
      `/auto-dorm-check/users${query(options)}`,
    );
  },
  autoDormCheckNow(userId: string) {
    return request<AutoDormCheckRunResult>(
      `/auto-dorm-check/users/${encodeURIComponent(userId)}/check-in`,
      { method: "POST" },
    );
  },
  autoDormCheckTasks(
    userId: string,
    options: { page?: number; pageSize?: number } = {},
  ) {
    return request<Paginated<AutoDormCheckTask>>(
      `/auto-dorm-check/users/${encodeURIComponent(userId)}/tasks${query(options)}`,
    );
  },
  autoDormCheckIns(
    userId: string,
    options: { page?: number; pageSize?: number } = {},
  ) {
    return request<Paginated<AutoDormCheckIn>>(
      `/auto-dorm-check/users/${encodeURIComponent(userId)}/check-ins${query(options)}`,
    );
  },
  setUserActivation(id: string, active: boolean) {
    return request<{
      accountActive: boolean;
      accountDeactivatedAt: string | null;
      accountDeactivationReason: string | null;
      revokedSessions: number;
    }>(`/users/${encodeURIComponent(id)}/activation`, {
      method: "PATCH",
      body: JSON.stringify({ active }),
    });
  },
  refreshUser(id: string) {
    return request<{
      resources: Array<{ resource: string; status: "updated" | "failed" }>;
    }>(`/users/${encodeURIComponent(id)}/refresh`, { method: "POST" });
  },
  revokeUserSessions(id: string) {
    return request<{ revoked: number }>(
      `/users/${encodeURIComponent(id)}/revoke-sessions`,
      { method: "POST" },
    );
  },
  revokeUserSession(id: string, sessionId: string) {
    return request<{ revoked: true }>(
      `/users/${encodeURIComponent(id)}/sessions/${encodeURIComponent(sessionId)}`,
      { method: "DELETE" },
    );
  },
  clearElectricityBinding(id: string) {
    return request<{ deleted: true }>(
      `/users/${encodeURIComponent(id)}/electricity-binding`,
      { method: "DELETE" },
    );
  },
  deleteCourseAssistantReview(userId: string, reviewId: string) {
    return request<{ deleted: true }>(
      `/users/${encodeURIComponent(userId)}/course-assistant/reviews/${encodeURIComponent(reviewId)}`,
      { method: "DELETE" },
    );
  },
  setCourseAssistantReviewActivation(
    userId: string,
    reviewId: string,
    active: boolean,
  ) {
    return request<{ active: boolean; deactivatedAt: string | null }>(
      `/users/${encodeURIComponent(userId)}/course-assistant/reviews/${encodeURIComponent(reviewId)}/activation`,
      { method: "PATCH", body: JSON.stringify({ active }) },
    );
  },
  deleteUser(id: string, account: string) {
    return request<{ deleted: boolean }>(
      `/users/${encodeURIComponent(id)}${query({ confirmAccount: account })}`,
      { method: "DELETE" },
    );
  },
  snapshotResources() {
    return request<SnapshotResource[]>("/snapshots/resources");
  },
  snapshotOwners(
    options: {
      page?: number;
      pageSize?: number;
      q?: string;
      resource?: string;
    } = {},
  ) {
    return request<Paginated<SnapshotOwner>>(
      `/snapshot-owners${query(options)}`,
    );
  },
  snapshots(
    options: {
      page?: number;
      pageSize?: number;
      q?: string;
      resource?: string;
    } = {},
  ) {
    return request<Paginated<SnapshotRow>>(`/snapshots${query(options)}`);
  },
  deleteSnapshot(userId: string, resourceKey: string) {
    return request<{ deleted: boolean }>(
      `/snapshots/${encodeURIComponent(userId)}/${encodeURIComponent(resourceKey)}?confirm=true`,
      { method: "DELETE" },
    );
  },
  jobs() {
    return request<JobState[]>("/jobs");
  },
  runJob(name: string) {
    return request<{
      accepted: boolean;
      reason?: "already_running";
      name: string;
    }>(`/jobs/${encodeURIComponent(name)}/run`, { method: "POST" });
  },
  calendars() {
    return request<CalendarRecord[]>("/calendars");
  },
  storage() {
    return request<StorageOverview>("/storage");
  },
  feedback(
    options: {
      page?: number;
      pageSize?: number;
      q?: string;
      status?: "pending" | "resolved";
      type?: FeedbackType;
    } = {},
  ) {
    return request<FeedbackResult>(`/feedback${query(options)}`);
  },
  setFeedbackResolved(id: string, resolved: boolean) {
    return request<UserFeedback>(`/feedback/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify({ resolved }),
    });
  },
  publications(
    options: {
      page?: number;
      pageSize?: number;
      q?: string;
      status?: "current" | "deleted" | "all";
      kind?: "announcement" | "notification";
    } = {},
  ) {
    return request<Paginated<PublicationSummary>>(
      `/publications${query(options)}`,
    );
  },
  publication(id: string) {
    return request<PublicationDetail>(
      `/publications/${encodeURIComponent(id)}`,
    );
  },
  publicationCourses(options: { q?: string } = {}) {
    return request<PublicationCourseCatalog>(
      `/publications/courses${query(options)}`,
    );
  },
  publicationRecipients(
    id: string,
    options: { page?: number; pageSize?: number } = {},
  ) {
    return request<Paginated<PublicationRecipient>>(
      `/publications/${encodeURIComponent(id)}/recipients${query(options)}`,
    );
  },
  createPublication(input: CreatePublicationInput, idempotencyKey: string) {
    return request<PublicationDetail>("/publications", {
      method: "POST",
      headers: { "Idempotency-Key": idempotencyKey },
      body: JSON.stringify(input),
    });
  },
  deletePublication(id: string) {
    return request<{ deleted: true }>(
      `/publications/${encodeURIComponent(id)}`,
      { method: "DELETE" },
    );
  },
  restorePublication(id: string) {
    return request<{ restored: true }>(
      `/publications/${encodeURIComponent(id)}/restore`,
      { method: "POST" },
    );
  },
  uploadPublicationImage(file: File) {
    return request<PublicationMedia>("/media", {
      method: "POST",
      headers: { "X-File-Name": encodeURIComponent(file.name) },
      body: file,
    });
  },
  audit(
    options: {
      page?: number;
      pageSize?: number;
      q?: string;
      level?: AuditLevel;
    } = {},
  ) {
    return request<AuditResult>(`/audit${query(options)}`);
  },
};
